class PriceRenderer extends HTMLElement {
  static #symbols = {};
  static #defaults = {};
  #container;
  #observer;

  static get observedAttributes() {
    return ["symbols", "lock"];
  }

  constructor() {
    super();

    this.setAttribute("data-property", "_p");

    const shadow = this.attachShadow({ mode: "closed" });
    const container = document.createElement("div");
    this.#container = container;
    shadow.appendChild(container);

    this.#calculate.bind(this);
  }

  connectedCallback() {
    this.lock(true);
    if (this.minOrder) {
      this.quantity = this.closest("form.js-add-to-cart-form").querySelector(
        ".js-qty-num"
      );
      this.quantity.addEventListener("input", (e) => {
        e.preventDefault();
        if (
          parseFloat(e.currentTarget.value) %
            parseFloat(e.currentTarget.step) ===
          0
        ) {
          this.release();
        } else {
          this.lock();
        }
      });
      $(this.quantity).on("change", (e) => {
        this.release();
      });
    }
    this.#observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes") {
          this.#container.innerText = this.#calculate();
          const variantId = parseInt(mutation.target.value);
          const variant = window.meta.product.variants.find(
            (variant) => variant.id === variantId
          );
          PriceRenderer.modifySymbol("p", variant.price);
        }
      });
    });
    this.#observer.observe(document.querySelector(".js-variant-id"), {
      attributes: true
    });
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === "symbols") {
      if (this.list) {
        try {
          PriceRenderer.modifySymbol(
            "p",
            this.list.find((d) =>
              Object.keys(PriceRenderer.#defaults).reduce(
                (result, key) =>
                  result && parseFloat(d[key]) === PriceRenderer.#symbols[key]
                    ? true
                    : false,
                true
              )
            ).p,
            false
          );
        } catch (e) {
          PriceRenderer.modifySymbol(
            "p",
            this.list.find((d) =>
              Object.keys(PriceRenderer.#defaults).reduce(
                (result, key) =>
                  result &&
                  (parseFloat(d[key]) === PriceRenderer.#symbols[key] ||
                    isNaN(parseFloat(d[key])))
                    ? true
                    : false,
                true
              )
            )?.p ||
              this.list.find((d) =>
                Object.keys(PriceRenderer.#defaults).reduce((result, key) => {
                  return result &&
                    (parseFloat(d[key]) === PriceRenderer.#defaults[key] ||
                      isNaN(parseFloat(d[key])))
                    ? true
                    : false;
                }, true)
              )?.p ||
              0,
            false
          );
        }
      }
      const price = this.#calculate();
      if (price && !isNaN(price)) {
        this.value = Math.ceil(price * 100) / 100;
        this.#container.innerText = Shopify.formatMoney(
          this.value * 100,
          document.body.dataset.moneyFormat
        );
        this.release(true);
      } else {
        this.lock(true);
        document
          .querySelector(".js-add-to-cart")
          ?.setAttribute("disabled", "true");
        const price = this.#calculate(
          PriceRenderer.#fillSymbols(
            PriceRenderer.#fillSymbols(
              this.getAttribute("equation"),
              PriceRenderer.#symbols
            ),
            PriceRenderer.#defaults
          )
        );
        this.#container.innerText =
          this.prefix +
          Shopify.formatMoney(
            Math.ceil(price * 100),
            document.body.dataset.moneyFormat
          );
      }
      if (this.minOrder && PriceRenderer.#symbols.b) {
        const step = Math.floor(
          parseFloat(this.minOrder.getAttribute("min")) /
            PriceRenderer.#symbols.b
        );
        this.quantity.step = step;
        this.quantity.min = step;
        this.quantity.value = step;
        this.release();
        this.quantity.setAttribute("data-min", step);
        $(this.quantity).data("min", step);
        this.minOrder.innerText = step;
        this.minOrder.value = step;
      }
      this.dispatchEvent(
        new CustomEvent("change", {
          bubbles: true,
          detail: {
            value: this.value
          }
        })
      );
    } else if (name === "lock") {
      if (this.lockState) {
        document.querySelector(".js-add-to-cart")?.removeAttribute("disabled");
      } else {
        document
          .querySelector(".js-add-to-cart")
          ?.setAttribute("disabled", "true");
      }
    }
  }

  get prefix() {
    return this.getAttribute("default-prefix");
  }

  get equation() {
    return PriceRenderer.#fillSymbols(this.getAttribute("equation"));
  }

  get list() {
    return window[this.getAttribute("list")];
  }

  get minOrder() {
    return document.querySelector("wc-min-order");
  }

  get lockState() {
    return !this.hardLock && !this.hasAttribute("lock");
  }

  lock(hardLock = false) {
    if (hardLock) {
      this.hardLock = hardLock;
    }
    this.setAttribute("lock", "");
  }
  static lock() {
    PriceRenderer.instances.forEach((d) => d.lock(true));
  }

  release(hardLock = false) {
    if (hardLock) {
      this.hardLock = false;
    }
    if (!this.hardLock) {
      this.removeAttribute("lock");
    }
  }

  static release() {
    PriceRenderer.instances.forEach((d) => d.release(true));
  }

  static get instances() {
    return Array.from(document.querySelectorAll("price-renderer"));
  }

  static get valid() {
    return PriceRenderer.symbols.reduce(
      (a, b) => a && PriceRenderer.getSymbol(b),
      true
    );
  }

  static get symbols() {
    return Object.keys(this.#defaults);
  }

  static getSymbol(symbol) {
    return this.#symbols[symbol];
  }

  static #fillSymbols(equation, defaults = {}) {
    return Object.entries(this.#symbols).reduce(
      (string, [key, value]) => string.replace(new RegExp(key, "g"), value),
      Object.entries(defaults).reduce(
        (string, [key, value]) => string.replace(new RegExp(key, "g"), value),
        equation
      )
    );
  }

  static registerSymbol(symbol, value) {
    this.#defaults[symbol] = value;
  }

  static modifySymbol(symbol, value, rerender = true) {
    this.#symbols[symbol] = value;
    rerender &&
      document
        .querySelectorAll("price-renderer")
        .forEach((d) => d.setAttribute("symbols", ""));
  }

  #calculate(equation = this.equation) {
    let tokens =
      equation.replace(/ /g, "").match(/[+\-\*\/]*(\d+\.?\d*|\w)/g) || [];
    // console.log("___start___");
    return (
      tokens.reduce((result) => {
        // console.log(tokens);
        let nextIndex = tokens.findIndex(
          (d, i) =>
            !isNaN(parseFloat(d) || d.slice(i <= 0 ? 0 : 1)) &&
            isNaN(d) &&
            (d?.startsWith("*") || d?.startsWith("/"))
        );
        let next;
        let prev;
        if (nextIndex >= 0) {
          next = tokens[nextIndex];
          prev = parseFloat(
            tokens[nextIndex - 1].slice(nextIndex <= 1 ? 0 : 1)
          );
          let result;
          switch (next) {
            case next.startsWith("*") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev * parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            case next.startsWith("/") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev / parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            default:
              break;
          }
          return result;
        }
        nextIndex = tokens.findIndex(
          (d, i) =>
            d.slice(i <= 0 ? 0 : 1) &&
            (d?.startsWith("+") || d?.startsWith("-"))
        );
        next;
        prev;
        if (nextIndex >= 0) {
          next = tokens[nextIndex];
          prev = parseFloat(
            tokens[nextIndex - 1].slice(nextIndex <= 1 ? 0 : 1)
          );
          let result;
          switch (next) {
            case next.startsWith("+") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev + parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            case next.startsWith("-") && next:
              result = tokens[nextIndex - 1] = `${tokens[nextIndex - 1].slice(
                0,
                nextIndex <= 1 ? 0 : 1
              )}${prev - parseFloat(next.slice(1))}`;
              tokens = tokens.filter((d, i) => i !== nextIndex);
              break;
            default:
              break;
          }
          let number = parseFloat(result);
          if (isNaN(number)) {
            result = NaN;
          } else {
            result = number;
          }
          return result;
        }
        return result;
      }, 0.0) || ""
    );
  }
}

customElements.define("price-renderer", PriceRenderer);
